import {
  DriverPayBaseEntityProjection,
  EntityProjectionWithSelf,
  PagingCollectionProjection,
} from 'app/driver-pay/persistence/implementation/DriverPayBaseEntity';
import { FormBuilder, Validators } from '@angular/forms';
import { EntityFormGroup } from 'app/driver-pay/persistence/core/EntityView';
import { inject, Injectable } from '@angular/core';
import { DriverPayBaseEntityServiceImpl } from 'app/driver-pay/persistence/implementation/Entity.service';
import { FacilityShallow } from 'app/driver-pay/persistence/implementation/facilities/Facilities';
import { parseTemplate } from 'url-template';

export type UserRole =
  | 'REGIONAL_PAY_CLERK'
  | 'TERMINAL_LEADER'
  | 'SPECIALIST'
  | 'MANAGER'
  | 'ADMINISTRATOR'
  | 'DRIVER'
  | 'COMPENSATION_TEAM';

export interface UserRoleShallow extends EntityProjectionWithSelf {
  role: UserRole;
}

export interface UserPartitionWithFacility extends EntityProjectionWithSelf {
  partition: FacilityShallow;
}

export interface UserRequestBody {
  principal: string;
  name?: string;
}

export interface UserProjectionWithRolesAndPartitions extends DriverPayBaseEntityProjection {
  principal: string;
  name?: string;
  userRoles: UserRoleShallow[];
  userPartitions: UserPartitionWithFacility[];
}

export interface UserRoleRequestBody {
  user: string;
  role: UserRole;
}

export interface UserPartitionRequestBody {
  user: string;
  partition: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserFormGroupProvider {
  private readonly formBuilder = inject(FormBuilder);

  userFormGroup(): EntityFormGroup<UserRequestBody> {
    const fg = this.formBuilder.group({
      principal: [null as string | null, Validators.required],
      name: [null as string | null],
    });

    fg.get('principal')?.valueChanges.subscribe((val) => {
      if (val) {
        fg.get('principal')?.setValue(val.toLowerCase(), { emitEvent: false });
      }
    });
    return fg;
  }

  userRoleFormGroup(): EntityFormGroup<UserRoleRequestBody> {
    return this.formBuilder.group({
      user: [null as string | null, Validators.required],
      role: [null as UserRole | null, Validators.required],
    });
  }

  userPartitionFormGroup(): EntityFormGroup<UserPartitionRequestBody> {
    return this.formBuilder.group({
      user: [null as string | null, Validators.required],
      partition: [null as string | null, Validators.required],
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class EntityServiceImplUsers extends DriverPayBaseEntityServiceImpl<
  UserProjectionWithRolesAndPartitions,
  UserProjectionWithRolesAndPartitions,
  UserRequestBody
> {
  controllerPath = '/users';
  override collectionProjection = 'userWithRolesAndPartitions';
  override itemProjection = 'userWithRolesAndPartitions';
  getCollectionFromPagedModel = (pagedModel: PagingCollectionProjection<UserProjectionWithRolesAndPartitions>) =>
    pagedModel._embedded['users'];
  populateRequestBodyFromItemProjection = (item: UserProjectionWithRolesAndPartitions): Partial<UserRequestBody> => {
    return item;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EntityServiceImplUserRoles extends DriverPayBaseEntityServiceImpl<
  UserRoleShallow,
  UserRoleShallow,
  UserRoleRequestBody
> {
  controllerPath = '/userRoles';
  override collectionProjection = 'userRoleShallow';
  override itemProjection = 'userRoleShallow';
  getCollectionFromPagedModel = (pagedModel: PagingCollectionProjection<UserRoleShallow>) =>
    pagedModel._embedded['userRoles'];
  populateRequestBodyFromItemProjection = (item: UserRoleShallow): Partial<UserRoleRequestBody> => {
    return item;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EntityServiceImplUserPartitions extends DriverPayBaseEntityServiceImpl<
  UserPartitionWithFacility,
  UserPartitionWithFacility,
  UserPartitionRequestBody
> {
  controllerPath = '/userPartitions';
  override collectionProjection = 'userPartitionWithFacility';
  override itemProjection = 'userPartitionWithFacility';
  getCollectionFromPagedModel = (pagedModel: PagingCollectionProjection<UserPartitionWithFacility>) =>
    pagedModel._embedded['userPartitions'];
  populateRequestBodyFromItemProjection = (item: UserPartitionWithFacility): Partial<UserPartitionRequestBody> => {
    return {
      partition: parseTemplate(item.partition._links.self.href).expand({}),
    };
  };
}
